import { useCallback, useEffect, useState } from 'react'
import { Check, Layers, RefreshCw, X } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { fetchModerationGroups, bulkResolveReports, type ModerationGroup } from './bulkDecisionApi'

/**
 * A6.20 — Ommaviy qaror. Bir xil kontent ko'p marta shikoyat qilinganда
 * (spam-flood), moderator BUTUN guruhni bir marta hal qiladi — birma-bir emas.
 * Backend har harakatni status-guarded atomik bajaradi (faqat pending qatorlar),
 * shuning uchun boshqa moderator yopgan ish qayta hal qilinmaydi.
 * Read-only A7.03 ReportGroups'дan farqli — bu ACT qatlami.
 */

const S = {
  title: 'Ommaviy qaror',
  desc: 'Bir xil kontentga ko’p shikoyatni bitta qaror bilan yoping (spam-flood)',
  category: 'Toifa',
  anyCategory: 'Barcha toifa',
  note: 'Izoh (ixtiyoriy)',
  refresh: 'Yangilash',
  empty: 'Kutilayotgan guruhlangan shikoyat yo’q',
  target: 'Obyekt',
  reports: 'Shikoyat',
  reporters: 'Shikoyatchi',
  categories: 'Toifalar',
  latest: 'Oxirgi',
  latestReason: 'Oxirgi sabab',
  resolve: 'Hal qilish',
  dismiss: 'Rad etish',
  confirmResolve: (n: number) => `${n} ta shikoyatni HAL QILINGAN deb yopasizmi?`,
  confirmDismiss: (n: number) => `${n} ta shikoyatni RAD ETASIZmi?`,
  done: (n: number) => `${n} ta shikoyat yopildi`,
  failed: 'Amal bajarilmadi',
}

const CAT_OPTIONS = ['spam', 'csam', 'violence', 'fraud', 'copyright', 'doxxing', 'other']

function fmtTime(ts: number): string {
  if (!Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

export function BulkDecision() {
  const { toast } = useToast()
  const [category, setCategory] = useState('')
  const [note, setNote] = useState('')
  const [rows, setRows] = useState<ModerationGroup[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null) // target key being acted on

  const load = useCallback(() => {
    setLoading(true)
    fetchModerationGroups(category, 100)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [category])

  useEffect(() => {
    load()
  }, [load])

  const act = useCallback(
    async (g: ModerationGroup, dismiss: boolean) => {
      const ok = window.confirm(dismiss ? S.confirmDismiss(g.report_count) : S.confirmResolve(g.report_count))
      if (!ok) return
      const key = `${g.target_type}:${g.target_id}`
      setBusy(key)
      try {
        const res = await bulkResolveReports(g.target_type, g.target_id, note, dismiss)
        toast(S.done(res.resolved), { tone: 'success' })
        // Yopilgan guruhni ro'yxatdan olib tashlaymiz (endi pending yo'q).
        setRows((prev) => (prev ? prev.filter((r) => `${r.target_type}:${r.target_id}` !== key) : prev))
      } catch (e) {
        toast(S.failed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
      } finally {
        setBusy(null)
      }
    },
    [note, toast],
  )

  const input = 'rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none'

  return (
    <>
      <PageHeader title={S.title} screenId="A6.20" description={S.desc} />
      <div className="flex flex-wrap items-end gap-2 border-b border-border-subtle p-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-tertiary">{S.category}</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={input}>
            <option value="">{S.anyCategory}</option>
            {CAT_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className="text-xs text-tertiary">{S.note}</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="…" className={input} />
        </label>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {S.refresh}
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null || loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <Layers size={24} className="mx-auto mb-2 opacity-60" />
            {S.empty}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-2 py-1.5 font-medium">{S.target}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{S.reports}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{S.reporters}</th>
                  <th className="px-2 py-1.5 font-medium">{S.categories}</th>
                  <th className="px-2 py-1.5 font-medium">{S.latestReason}</th>
                  <th className="px-2 py-1.5 font-medium">{S.latest}</th>
                  <th className="px-2 py-1.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => {
                  const key = `${g.target_type}:${g.target_id}`
                  const isBusy = busy === key
                  return (
                    <tr key={key} className="border-b border-border-subtle align-top">
                      <td className="whitespace-nowrap px-2 py-1.5 font-mono text-primary">{g.target_type}#{g.target_id}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-medium text-warning">{g.report_count}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-secondary">{g.reporter_count}</td>
                      <td className="px-2 py-1.5 text-secondary">{(g.categories ?? []).join(', ') || '—'}</td>
                      <td className="max-w-xs truncate px-2 py-1.5 text-secondary" title={g.latest_reason ?? ''}>{g.latest_reason ?? '—'}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(g.latest_at)}</td>
                      <td className="whitespace-nowrap px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => void act(g, false)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                          >
                            <Check size={12} />
                            {S.resolve}
                          </button>
                          <button
                            onClick={() => void act(g, true)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-xs font-medium text-secondary hover:text-primary disabled:opacity-50"
                          >
                            <X size={12} />
                            {S.dismiss}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
